import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Generic modal wrapper. Fixed overlay with a centered card. Header has a
 * small uppercase kicker, a bold title, and a close button; body scrolls
 * if it overflows.
 *
 * Why a generic wrapper rather than three nearly-identical modal shells:
 *   - Escape-to-close behaviour, click-outside-to-close behaviour, and the
 *     dark overlay treatment are all things we want consistent across the
 *     three modals.
 *   - The Header / Body / Footer split keeps modal content clean — each
 *     modal just hands ReactNodes for the body and (optionally) footer.
 *
 * The modal expects to be conditionally mounted by the parent (i.e., the
 * parent renders `{open && <Modal …/>}`). When open, mounts; when closed,
 * unmounts. This avoids the "stale modal state" issue where re-opening
 * shows the previous fill.
 */
interface ModalProps {
  kicker?: string;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Max width in pixels. Default 640. */
  maxWidth?: number;
}

export function Modal({
  kicker,
  title,
  onClose,
  children,
  footer,
  maxWidth = 640,
}: ModalProps) {
  // Escape to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(26, 24, 21, 0.55)' }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-md shadow-xl flex flex-col bg-cream-2 border border-cream-border"
        style={{ maxWidth, maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between px-6 py-4 border-b border-cream-border">
          <div>
            {kicker && (
              <div className="text-[10px] uppercase tracking-widest text-ink-2">{kicker}</div>
            )}
            <h3 className="text-lg font-bold mt-0.5 text-ink">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-cream rounded text-ink-2"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-4">{children}</div>
        {footer && (
          <footer className="px-6 py-4 border-t border-cream-border bg-cream">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
